import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/BusHubList.jsx
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const PAGE_SIZE = 20;

export default function BusHubList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // 검색 필터
  const [filters, setFilters] = useState({
    title: "",
    author: "",
    company: "",
    car_count: "",
    region: "",
  });

  // 목록 & 페이지네이션
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // 정렬: 최신순(등록일 desc)
  const [orderBy, setOrderBy] = useState("created_at_desc");

  // 로그인 사용자 (권한: first/operator 접근/표시)
  const role = localStorage.getItem("role"); // 로그인 시 저장해둔 profiles.role
  const email = localStorage.getItem("email");
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setCurrentUser(user || null));
  }, []);

  // 권한 체크: first/operator 접근 가능
  useEffect(() => {
    if (role !== "first" && role !== "operator") {
      showUserAlert("접근 권한이 없습니다.");
      navigate("/reservations");
    }
  }, [role, navigate]);

  const fetchList = async (opts = { page: 1 }) => {
    setLoading(true);
    try {
      const from = (opts.page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE;

      // 관계형 count: applications:bus_hub_applications(count)
      let query = supabase
        .from("bus_hub_posts")
        .select("id, title, author, author_id, company, company_name, region, car_count, price, views, created_at, start_at, end_at, status, applications:bus_hub_applications(count)")

        

      // 검색 필터
      if (filters.title) query = query.ilike("title", `%${filters.title}%`);
      if (filters.author) query = query.ilike("author", `%${filters.author}%`);
      if (filters.company) query = query.ilike("company_name", `%${filters.company}%`);
      if (filters.region) query = query.ilike("region", `%${filters.region}%`);
      if (filters.car_count) {
        const n = Number(filters.car_count);
        if (!Number.isNaN(n)) query = query.eq("car_count", n);
      }

      switch (orderBy) {
        case "created_at_desc":
          query = query.order("created_at", { ascending: false, nullsFirst: false });
          break;
        case "created_at_asc":
          query = query.order("created_at", { ascending: true,  nullsFirst: true });
          break;
        case "views_desc":
          query = query.order("views", { ascending: false, nullsFirst: false });
          // 보조정렬(동점시 최신 우선) - 선택
          query = query.order("created_at", { ascending: false });
          break;
        case "views_asc":
          query = query.order("views", { ascending: true, nullsFirst: true });
          query = query.order("created_at", { ascending: false }); // 선택
          break;
        case "start_at_asc":
          query = query.order("start_at", { ascending: true, nullsFirst: true });
          query = query.order("id", { ascending: false }); // 선택(안정적 순서)
          break;
        case "start_at_desc":
          query = query.order("start_at", { ascending: false, nullsFirst: false });
          query = query.order("id", { ascending: false }); // 선택
          break;
        default:
          query = query.order("created_at", { ascending: false });
      }

      // range
      query = query.range(from, to - 1);

      const { data, error } = await query;
      if (process.env.NODE_ENV !== "production") console.log("fetched bus_hub_posts:", data, error);
      if (error) throw error;

      // 다음 페이지 존재 여부 추정: PAGE_SIZE보다 많이 가져왔는지 점검(혹은 count를 별도 쿼리)
      setRows(data || []);
      setHasMore((data || []).length === PAGE_SIZE);
      setPage(opts.page);
    } catch (e) {
      console.error("bus_hub_posts fetch error:", e.message);
      setRows([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy]);

  const handleSearch = () => fetchList({ page: 1 });

  const statusBadge = (status) => {
    const base = "px-2 py-0.5 rounded text-xs font-semibold";
    switch (status) {
      case "open": return <span className={`${base} bg-emerald-100 text-emerald-700`}>모집중</span>;
      case "accepted": return <span className={`${base} bg-blue-100 text-blue-700`}>선정됨</span>;
      case "payment_held": return <span className={`${base} bg-amber-100 text-amber-700`}>결제 보류(PG)</span>;
      case "in_progress": return <span className={`${base} bg-indigo-100 text-indigo-700`}>진행중</span>;
      case "cancelled": return <span className={`${base} bg-red-100 text-red-700`}>취소</span>;
      case "settled": return <span className={`${base} bg-gray-200 text-gray-700`}>정산완료</span>;
      default: return <span className={`${base} bg-gray-100 text-gray-600`}>상태없음</span>;
    }
  };

  // KST로 보기 좋게 포맷
  const fmtKST = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("ko-KR", {
      timeZone: "Asia/Seoul",
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const dateRangeText = (row) => {
    const s = fmtKST(row.start_at);
    const e = fmtKST(row.end_at);
    return s && e ? `${s} ~ ${e}` : "-";
  };

  const applicationCount = (row) => {
    // Supabase 관계 count: applications[0].count 형식으로 옴
    const rel = Array.isArray(row.applications) ? row.applications[0] : null;
    return rel?.count ?? 0;
  };

  const columns = useMemo(
    () => [
      { key: "title", label: "제목[신청수]", className: "w-[32%]" },
      { key: "car_count", label: "차량 대수", className: "w-20" },
      { key: "region", label: "지역", className: "w-16" },
      { key: "status", label: "상태", className: "w-24" },
      { key: "period", label: "일시(시작~종료)", className: "w-72" },
      { key: "price", label: "금액", className: "w-24" },
      { key: "action", label: "관리", className: "w-32" },
      { key: "created_at", label: "등록일", className: "w-24" },
      { key: "views", label: "조회", className: "w-16" },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <Header />

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">버스 허브</h2>
          <div className="flex gap-2">
            <select
              className="border rounded px-2 py-1 text-sm"
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value)}
              disabled={loading}
            >
              <option value="created_at_desc">최신순</option>
              <option value="created_at_asc">등록 오래된순</option>
              <option value="views_desc">조회 많은순</option>
              <option value="start_at_asc">시작 빠른순</option>
              <option value="start_at_desc">시작 늦은순</option>
            </select>
          </div>
        </div>

        {/* 검색 */}
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-medium mb-1">제목</label>
            <Input
              value={filters.title}
              onChange={(e) => setFilters({ ...filters, title: e.target.value })}
              placeholder="제목 검색"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">작성자(이메일)</label>
            <Input
              value={filters.author}
              onChange={(e) => setFilters({ ...filters, author: e.target.value })}
              placeholder="작성자 이메일"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">작성 회사</label>
            <Input
              value={filters.company}
              onChange={(e) => setFilters({ ...filters, company: e.target.value })}
              placeholder="회사명"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">차량 대수</label>
            <Input
              value={filters.car_count}
              onChange={(e) => setFilters({ ...filters, car_count: e.target.value })}
              placeholder="예: 2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">지역</label>
            <Input
              value={filters.region}
              onChange={(e) => setFilters({ ...filters, region: e.target.value })}
              placeholder="예: 서울/부산"
            />
          </div>
          <Button onClick={handleSearch} disabled={loading}>검색</Button>
        </div>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white">
            <div className="overflow-x-auto">
              <table className="table-fixed w-full border text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key} className={`border px-2 py-0.5 text-center leading-tight ${c.className || ""}`}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      {/* 제목[신청수] → 상세로 이동 */}
                      <td className="border px-2 py-0.5 leading-tight">
                        <button
                          className="text-left text-blue-600 underline break-words"
                          onClick={() => navigate(`/bus-hub/${row.id}`)}
                          title="상세 보기"
                        >
                          {row.title} [{applicationCount(row)}]
                        </button>
                      </td>
                      <td className="border px-2 py-0.5 text-center leading-tight">{row.car_count}</td>
                      <td className="border px-2 py-0.5 text-center leading-tight">{row.region}</td>
                      <td className="border px-2 py-0.5 text-center leading-tight">{statusBadge(row.status)}</td>
                      <td className="border px-2 py-0.5 text-center leading-tight">{dateRangeText(row)}</td>
                      <td className="border px-2 py-0.5 text-center leading-tight">{(row.price ?? 0).toLocaleString()}원</td>
                      <td className="border px-2 py-0.5 text-center leading-tight">
                        <div className="flex justify-center gap-2">
                          {/* 신청 현황/선정/결제로 가는 관리 페이지(작성자만 노출 가정) */}
                          {(row.author_id === currentUser?.id || row.author === email) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 py-0 text-xs"
                              onClick={() => navigate(`/bus-hub/${row.id}/manage`)}
                            >
                              신청/결제 관리
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="border px-2 py-0.5 text-center leading-tight">{(row.created_at || "").slice(0, 10)}</td>
                      <td className="border px-2 py-0.5 text-center leading-tight">{row.views ?? 0}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && !loading && (
                    <tr>
                      <td className="border p-4 text-center text-gray-500" colSpan={columns.length}>
                        게시글이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div className="flex justify-center gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchList({ page: Math.max(1, page - 1) })}
                disabled={loading || page === 1}
              >
                이전
              </Button>
              <span className="px-2 text-sm">[{page}]</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchList({ page: page + 1 })}
                disabled={loading || !hasMore}
              >
                다음
              </Button>            
              <Button onClick={() => navigate("/bus-hub/new")}>글쓰기</Button>
            </div>
          </CardContent>
        </Card>

        {/* 흐름 안내(도움말) */}
        <div className="text-xs text-gray-600">
          <p>• A(작성자)가 글 게시 → B/C 등 신청 → A가 B를 선정하고 결제 진행 → PG 보류(PG사 에스크로) → 
            <span className="ml-1">① 정상 완료 시 정산, ② 시작 전 취소시 전액 취소, ③ 진행 중 취소시 일부 정산</span>
          </p>
        </div>
      </div>
    </div>
  );
}
